import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../ui/card";

const AboutCtrlP: React.FC = () => {
  const [showSection1, setShowSection1] = useState(false);
  const [showSection2, setShowSection2] = useState(false);

  const text1 = "CTRL+P is a cloud-based document printing platform that connects students and professionals with local print shops. Our service allows you to upload documents, customize print settings, pay online, and pick up your prints at your convenience - all through a simple, user-friendly web interface.";
  
  const text2 = "CTRL+P is revolutionizing document printing for students and professionals by making it quick, easy, and hassle-free. Save valuable time by eliminating long queues at traditional print shops, reduce stress with our simple upload process, and enjoy the flexibility to print from anywhere at any time. With CTRL+P, you can focus on what matters while we handle your printing needs efficiently.";

  useEffect(() => {
    const timer1 = setTimeout(() => {
      setShowSection1(true);
    }, 500);

    const timer2 = setTimeout(() => {
      setShowSection2(true);
    }, 1000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);
  const benefits = [
    {
      icon: "zap",
      title: "Fast & Efficient",
      description: "Print your documents in minutes without waiting in long queues."
    },
    {
      icon: "map",
      title: "Conveniently Located",
      description: "Access our printing services at partner locations across your campus or city."
    },
    {
      icon: "shield",
      title: "Secure & Private",
      description: "Your documents are encrypted and automatically deleted after printing."
    },
    {
      icon: "award",
      title: "High Quality",
      description: "Professional quality prints for all your academic and professional needs."
    }
  ];

  return (
    <section id="about-us" className="py-24 px-4 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-20">
          <span className="text-blue-600 font-extrabold text-6xl uppercase tracking-wider mb-16 block shadow-sm drop-shadow-sm animate-fade-in">
            About Us
          </span>
        </div>

        {/* What is CTRL+P Section */}
        <div className={`transition-all duration-1000 ${showSection1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="mb-24">
            <div className="text-center mb-12">
              <h2 className="text-5xl font-bold mb-8 leading-tight">
                What is{' '}
                <span className="text-blue-600 relative">
                  CTRL+P
                  <span className="absolute bottom-1 left-0 w-full h-1 bg-blue-100 -z-10"></span>
                </span>
                ?
              </h2>
            </div>
            
            <div className="max-w-6xl mx-auto">
              <div className="relative bg-gradient-to-r from-blue-50 via-white to-blue-50 p-8 rounded-2xl shadow-lg border border-blue-200 transform hover:scale-[1.02] transition-all duration-300">
                <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-blue-400 to-blue-600 rounded-l-2xl"></div>
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-blue-100 rounded-full opacity-30"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue-200 rounded-full opacity-30"></div>
                
                <p className="text-gray-700 text-xl leading-relaxed font-normal relative z-10 text-center px-4">
                  {text1}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Why Choose CTRL+P Section */}
        <div className={`transition-all duration-1000 ${showSection2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="mb-24 p-8 border-2 border-gray-300 rounded-3xl">
            <div className="text-center mb-12">
              <h2 className="text-5xl font-bold mb-8 leading-tight">
                Why Choose{' '}
                <span className="text-blue-600 relative">
                  CTRL+P
                  <span className="absolute bottom-1 left-0 w-full h-1 bg-blue-100 -z-10"></span>
                </span>
                ?
              </h2>
            </div>
            
            <div className="max-w-6xl mx-auto mb-16">
              <div className="relative bg-gradient-to-br from-white via-blue-50 to-white p-8 rounded-2xl shadow-lg border border-blue-20 transform hover:scale-[1.02] transition-all duration-300">
                <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-blue-400 to-blue-600 rounded-l-2xl"></div>
                <div className="absolute top-0 right-0 w-24 h-24 bg-blue-100 rounded-full opacity-20 transform translate-x-12 -translate-y-12"></div>
                <div className="absolute bottom-0 left-0 w-20 h-20 bg-blue-200 rounded-full opacity-20 transform -translate-x-10 translate-y-10"></div>
                
                <p className="text-gray-700 text-xl leading-relaxed font-normal relative z-10 text-center px-4">
                  {text2}
                </p>
              </div>
            </div>

            {/* Benefits Grid */}
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 max-w-6xl mx-auto ">
                {benefits.map((benefit, index) => (
                  <div 
                    key={index} 
                    className="text-center p-6 transition-all duration-300 hover:transform hover:scale-110 hover:shadow-lg rounded-xl bg-white/50 backdrop-blur-sm border border-blue-200 hover:border-blue-300"
                    style={{ animationDelay: `${index * 0.2}s` }}
                  >
                    <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-5 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-md hover:shadow-xl transition-all duration-300">
                      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600">
                        {benefit.icon === "zap" && <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />}
                        {benefit.icon === "map" && (
                          <>
                            <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
                            <line x1="8" y1="2" x2="8" y2="18" />
                            <line x1="16" y1="6" x2="16" y2="22" />
                          </>
                        )}
                        {benefit.icon === "shield" && <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />}
                        {benefit.icon === "award" && (
                          <>
                            <circle cx="12" cy="8" r="7" />
                            <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
                          </>
                        )}
                      </svg>
                    </div>
                    <h4 className="font-bold text-lg text-gray-800 mb-2">{benefit.title}</h4>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section - Printing Made Simple */}
        <div className="max-w-6xl mx-auto bg-gradient-to-br from-white via-blue-50 to-white rounded-3xl shadow-xl p-10 border border-blue-100 hover:shadow-2xl transition-all duration-500">
          <div className="flex flex-col justify-center">
            <h3 className="text-5xl font-bold mb-8 text-center leading-tight">
              Printing Made <span className="text-blue-600 relative">
                Simple
                <span className="absolute bottom-1 left-0 w-full h-1 bg-blue-100 -z-10"></span>
              </span>
            </h3>
            
            <div className="relative bg-gradient-to-r from-white to-blue-50 p-8 rounded-2xl mb-10 border border-blue-100 shadow-md">
              <div className="absolute -top-4 -left-4 w-8 h-8 bg-blue-200 rounded-full opacity-50"></div>
              <div className="absolute -bottom-4 -right-4 w-8 h-8 bg-blue-200 rounded-full opacity-50"></div>
              <p className="text-gray-700 text-center text-xl leading-relaxed font-normal relative z-10">
                CTRL+P offers a seamless solution that puts printing control back in your hands.
                Our platform connects you to nearby print shops, allowing you to upload, pay for, and collect your prints without hassle.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {benefits.map((benefit, index) => (
                <Card 
                  key={index} 
                  className="group relative overflow-hidden rounded-2xl border border-blue-200 bg-gradient-to-br from-white via-blue-50/30 to-white hover:shadow-2xl transition-all duration-500 transform hover:scale-[1.03] hover:-translate-y-1"
                >
                  {/* Left accent border - only visible on hover */}
                  <div className="absolute left-0 top-0 w-0 h-full bg-gradient-to-b from-blue-400 to-blue-600 group-hover:w-2 transition-all duration-300 rounded-l-2xl"></div>
                  
                  {/* Background decoration */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-blue-100 rounded-full opacity-10 transform translate-x-10 -translate-y-10 group-hover:opacity-20 transition-opacity duration-300"></div>
                  
                  <CardContent className="relative p-8">
                    <div className="flex items-start space-x-6">
                      <div className="flex-shrink-0">
                        <div className="relative p-4 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="relative z-10 text-blue-600 group-hover:text-blue-700 transition-colors duration-300">
                            {benefit.icon === "zap" && (
                              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                            )}
                            {benefit.icon === "map" && (
                              <>
                                <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
                                <line x1="8" y1="2" x2="8" y2="18" />
                                <line x1="16" y1="6" x2="16" y2="22" />
                              </>
                            )}
                            {benefit.icon === "shield" && (
                              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            )}
                            {benefit.icon === "award" && (
                              <>
                                <circle cx="12" cy="8" r="7" />
                                <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
                              </>
                            )}
                          </svg>
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="text-2xl font-bold mb-4 text-gray-800 group-hover:text-blue-700 transition-colors duration-300 leading-tight">
                          {benefit.title}
                        </h4>
                        <p className="text-gray-600 leading-relaxed font-normal text-lg group-hover:text-gray-700 transition-colors duration-300">
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                    
                    {/* Hover gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-50/0 via-blue-100/0 to-blue-50/0 group-hover:from-blue-50/20 group-hover:via-blue-100/10 group-hover:to-blue-50/20 transition-all duration-500 rounded-2xl pointer-events-none"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutCtrlP;
